import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

// Anonymous cart stored in localStorage: [{product_id, quantity}]
const LS_KEY = "makhana_cart";

function loadLocal() {
    try {
        return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    } catch {
        return [];
    }
}
function saveLocal(items) {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]); // array of {product_id, quantity, product?}
    const [loading, setLoading] = useState(false);

    const hydrateItems = useCallback(async (rawItems) => {
        if (!rawItems?.length) return [];
        // Fetch products in parallel (simple loop, backend returns quickly)
        const results = await Promise.all(
            rawItems.map(async (it) => {
                try {
                    const { data } = await api.get("/products", { params: {} });
                    const product = data.find((p) => p.id === it.product_id);
                    return product ? { ...it, product } : null;
                } catch {
                    return null;
                }
            })
        );
        return results.filter(Boolean);
    }, []);

    const refreshServer = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/cart");
            const hydrated = await hydrateItems(data.items || []);
            setItems(hydrated);
        } catch {}
    }, [user, hydrateItems]);

    const refreshLocal = useCallback(async () => {
        const local = loadLocal();
        const hydrated = await hydrateItems(local);
        setItems(hydrated);
    }, [hydrateItems]);

    useEffect(() => {
        setLoading(true);
        (async () => {
            if (user) {
                // Merge local into server on login
                const local = loadLocal();
                if (local.length) {
                    for (const it of local) {
                        try {
                            await api.post("/cart/add", { product_id: it.product_id, quantity: it.quantity });
                        } catch {}
                    }
                    saveLocal([]);
                }
                await refreshServer();
            } else {
                await refreshLocal();
            }
            setLoading(false);
        })();
    }, [user, refreshServer, refreshLocal]);

    const addToCart = async (product, quantity = 1) => {
        if (user) {
            await api.post("/cart/add", { product_id: product.id, quantity });
            await refreshServer();
        } else {
            const local = loadLocal();
            const existing = local.find((i) => i.product_id === product.id);
            if (existing) existing.quantity += quantity;
            else local.push({ product_id: product.id, quantity });
            saveLocal(local);
            await refreshLocal();
        }
        toast.success(`${product.name} added to cart`);
    };

    const updateQuantity = async (productId, quantity) => {
        if (user) {
            await api.put("/cart/update", { product_id: productId, quantity });
            await refreshServer();
        } else {
            let local = loadLocal();
            if (quantity <= 0) local = local.filter((i) => i.product_id !== productId);
            else {
                const e = local.find((i) => i.product_id === productId);
                if (e) e.quantity = quantity;
            }
            saveLocal(local);
            await refreshLocal();
        }
    };

    const removeFromCart = async (productId) => {
        if (user) {
            await api.delete(`/cart/remove/${productId}`);
            await refreshServer();
        } else {
            const local = loadLocal().filter((i) => i.product_id !== productId);
            saveLocal(local);
            await refreshLocal();
        }
        toast.success("Removed from cart");
    };

    const clearCart = async () => {
        if (user) {
            await api.delete("/cart/clear");
            await refreshServer();
        } else {
            saveLocal([]);
            setItems([]);
        }
    };

    const subtotal = items.reduce((acc, i) => acc + (i.product?.price || 0) * i.quantity, 0);
    const totalQty = items.reduce((acc, i) => acc + i.quantity, 0);

    return (
        <CartContext.Provider
            value={{ items, loading, addToCart, updateQuantity, removeFromCart, clearCart, subtotal, totalQty }}
        >
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
